import { Component } from '@theme/component';

/**
 * Mobile announcement carousel: horizontal slides with optional autoplay and arrow controls.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [scroller]
 * @property {HTMLElement[]} [slides]
 */
class AnnouncementBar extends Component {
  /** @type {number} */
  #index = 0;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#syncAria();
    this.#syncScroll(false);

    if (this.#autoplayMs > 0 && this.#slideElements.length > 1) {
      this.#startAutoplay();
    }

    const scroller = this.#scrollerEl;
    scroller?.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#scrollerEl?.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    super.disconnectedCallback();
  }

  /** @returns {HTMLElement | null} */
  get #scrollerEl() {
    const r = this.refs.scroller;
    return r instanceof HTMLElement ? r : null;
  }

  /** @returns {HTMLElement[]} */
  get #slideElements() {
    const slides = this.refs.slides;
    const list = Array.isArray(slides) ? slides : slides ? [slides] : [];
    return /** @type {HTMLElement[]} */ (list.filter((el) => el instanceof HTMLElement));
  }

  get #autoplayMs() {
    const raw = this.getAttribute('autoplay');
    if (raw == null || raw === '') return 0;
    const sec = parseInt(raw, 10);
    return Number.isFinite(sec) && sec > 0 ? sec * 1000 : 0;
  }

  /** @param {Event} [_event] */
  next(_event) {
    const slides = this.#slideElements;
    if (slides.length < 2) return;
    this.#index = (this.#index + 1) % slides.length;
    this.#syncScroll(true);
  }

  /** @param {Event} [_event] */
  previous(_event) {
    const slides = this.#slideElements;
    if (slides.length < 2) return;
    this.#index = (this.#index - 1 + slides.length) % slides.length;
    this.#syncScroll(true);
  }

  /**
   * @param {boolean} animate
   */
  #syncScroll(animate) {
    const scroller = this.#scrollerEl;
    if (!scroller) return;

    const w = scroller.clientWidth;
    scroller.scrollTo({
      left: this.#index * w,
      behavior: animate ? 'smooth' : 'auto',
    });

    this.#syncAria();
  }

  #syncAria() {
    this.#slideElements.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.#index ? 'false' : 'true');
    });
  }

  #startAutoplay() {
    this.#stopAutoplay();
    this.#timer = setInterval(() => this.next(), this.#autoplayMs);
  }

  #stopAutoplay() {
    if (this.#timer != null) {
      clearInterval(this.#timer);
      this.#timer = undefined;
    }
  }

  #onVisibility = () => {
    if (document.hidden) {
      this.#stopAutoplay();
    } else if (this.#autoplayMs > 0 && this.#slideElements.length > 1) {
      this.#startAutoplay();
    }
  };

  #scrollRaf = 0;

  #onScroll = () => {
    const scroller = this.#scrollerEl;
    if (!scroller) return;

    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      const w = scroller.clientWidth;
      if (w <= 0) return;
      const nextIndex = Math.round(scroller.scrollLeft / w);
      const max = Math.max(0, this.#slideElements.length - 1);
      this.#index = Math.min(max, Math.max(0, nextIndex));
      this.#syncAria();
    });
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
